if (!customElements.get('pnx-article-bundle-purchase')) {
  class PnxArticleBundlePurchase extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.connected = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.classList.add('pnx-ab--ready');
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('keydown', this.handleKeydown);
      this.rows.forEach((row) => this.syncRow(row));
      this.refreshSubmit();
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('keydown', this.handleKeydown);
      this.connected = false;
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-pnx-ab-row]'));
    }

    get submitButton() {
      return this.querySelector('[data-pnx-ab-submit]');
    }

    get cartDrawerElement() {
      return document.querySelector('cart-drawer');
    }

    quantityInput(row) {
      return row.querySelector('[data-pnx-ab-qty]');
    }

    selectedVariant(row) {
      const radio = row.querySelector('[data-pnx-ab-variant-radio]:checked');
      if (radio) {
        return {
          id: radio.value,
          available: radio.dataset.available === 'true' && !radio.disabled,
        };
      }

      const select = row.querySelector('[data-pnx-ab-variant]');
      if (select) {
        const option = select.options[select.selectedIndex];
        if (!option || !option.value) return null;
        return {
          id: option.value,
          available: option.dataset.available === 'true' && !option.disabled,
        };
      }

      const single = row.querySelector('[data-pnx-ab-variant-single]');
      if (single && single.dataset.variantId) {
        return {
          id: single.dataset.variantId,
          available: single.dataset.available === 'true',
        };
      }

      return null;
    }

    sanitizeQuantity(input) {
      if (!input) return 0;
      const max = parseInt(input.max, 10) > 0 ? parseInt(input.max, 10) : 9999;
      let value = Math.floor(parseFloat(input.value));
      if (!Number.isFinite(value) || value < 0) value = 0;
      if (value > max) value = max;
      if (String(value) !== input.value) input.value = String(value);
      return value;
    }

    rowQuantity(row) {
      return this.sanitizeQuantity(this.quantityInput(row));
    }

    syncRow(row) {
      const variant = this.selectedVariant(row);
      const available = !!(variant && variant.available);
      const variantId = variant ? String(variant.id) : '';

      row.querySelectorAll('[data-pnx-ab-stock]').forEach((el) => {
        el.hidden = el.dataset.pnxAbStock !== variantId;
      });
      row.querySelectorAll('[data-pnx-ab-unit-price]').forEach((el) => {
        el.hidden = el.dataset.pnxAbUnitPrice !== variantId;
      });
      row.classList.toggle('pnx-article-bundle__row--soldout', !available);

      const input = this.quantityInput(row);
      row.querySelectorAll('[data-pnx-ab-minus], [data-pnx-ab-plus]').forEach((button) => {
        button.disabled = !available || this.busy;
      });
      if (input) {
        input.disabled = !available || this.busy;
        if (!available) input.value = '0';
        this.sanitizeQuantity(input);
      }
    }

    refreshSubmit() {
      const button = this.submitButton;
      if (!button) return;
      const selectedRows = this.rows.filter((row) => {
        const variant = this.selectedVariant(row);
        return variant && variant.available && this.rowQuantity(row) > 0;
      });
      button.disabled = this.busy || selectedRows.length === 0;
    }

    collectItems() {
      return this.rows
        .map((row) => {
          const variant = this.selectedVariant(row);
          const quantity = this.rowQuantity(row);
          if (!variant || !variant.available || quantity <= 0) return null;
          return {
            id: variant.id,
            quantity: quantity,
            title: row.dataset.productTitle || '',
            row: row,
          };
        })
        .filter(Boolean);
    }

    handleClick(event) {
      const minus = event.target.closest('[data-pnx-ab-minus]');
      const plus = event.target.closest('[data-pnx-ab-plus]');
      const submit = event.target.closest('[data-pnx-ab-submit]');

      if (minus || plus) {
        const row = event.target.closest('[data-pnx-ab-row]');
        const input = row && this.quantityInput(row);
        if (!input || input.disabled || this.busy) return;
        const current = this.sanitizeQuantity(input);
        input.value = String(plus ? current + 1 : Math.max(0, current - 1));
        this.sanitizeQuantity(input);
        this.clearMessages();
        this.refreshSubmit();
        return;
      }

      if (submit) {
        event.preventDefault();
        this.addSelectedToCart();
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-pnx-ab-variant], [data-pnx-ab-variant-radio]')) {
        const row = event.target.closest('[data-pnx-ab-row]');
        if (row) this.syncRow(row);
        this.clearMessages();
        this.refreshSubmit();
        return;
      }

      if (event.target.matches('[data-pnx-ab-qty]')) {
        this.sanitizeQuantity(event.target);
        this.clearMessages();
        this.refreshSubmit();
      }
    }

    handleInput(event) {
      if (!event.target.matches('[data-pnx-ab-qty]')) return;
      const value = parseInt(event.target.value, 10);
      if (Number.isFinite(value) && value > 0) {
        const button = this.submitButton;
        if (button) button.disabled = this.busy;
      } else {
        this.refreshSubmit();
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Enter' || !event.target.matches('[data-pnx-ab-qty]')) return;
      event.preventDefault();
      this.sanitizeQuantity(event.target);
      this.refreshSubmit();
      const button = this.submitButton;
      if (button && !button.disabled) this.addSelectedToCart();
    }

    setLoading(isLoading) {
      this.busy = isLoading;
      const button = this.submitButton;
      if (button) {
        button.classList.toggle('is-loading', isLoading);
        if (isLoading) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      }

      this.querySelectorAll('[data-pnx-ab-qty], [data-pnx-ab-variant], [data-pnx-ab-variant-radio], [data-pnx-ab-minus], [data-pnx-ab-plus]').forEach((el) => {
        if (isLoading) {
          el.dataset.pnxAbWasDisabled = el.disabled ? 'true' : 'false';
          el.disabled = true;
        } else if (el.dataset.pnxAbWasDisabled !== undefined) {
          el.disabled = el.dataset.pnxAbWasDisabled === 'true';
          delete el.dataset.pnxAbWasDisabled;
        }
      });

      if (!isLoading) this.rows.forEach((row) => this.syncRow(row));
      this.refreshSubmit();
    }

    joinMessages() {
      return Array.from(arguments)
        .filter((part) => typeof part === 'string' && part.trim() !== '')
        .join(' ');
    }

    clearMessages() {
      this.querySelectorAll('[data-pnx-ab-status], [data-pnx-ab-error]').forEach((el) => {
        el.hidden = true;
        el.textContent = '';
      });
      this.rows.forEach((row) => row.classList.remove('pnx-article-bundle__row--error'));
    }

    showStatus(message) {
      const el = this.querySelector('[data-pnx-ab-status]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    showError(message, failedTitles) {
      const el = this.querySelector('[data-pnx-ab-error]');
      if (el) {
        el.textContent = message || '';
        el.hidden = !message;
      }
      const titles = Array.isArray(failedTitles) ? failedTitles : [];
      this.rows.forEach((row) => {
        const title = row.dataset.productTitle || '';
        row.classList.toggle('pnx-article-bundle__row--error', titles.includes(title));
      });
    }

    async syncCartState() {
      const routes = window.FoxTheme && window.FoxTheme.routes;
      const utils = window.FoxTheme && window.FoxTheme.utils;
      if (!routes || !routes.cart_url || !utils || !window.FoxTheme.pubsub) return;
      try {
        const response = await fetch(String(routes.cart_url), { ...utils.fetchConfig('json', 'GET') });
        const cartJson = await response.json();
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        if (this.cartDrawerElement) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        }
      } catch (error) {
        // 通信結果不明時は入力値を保持し、画面上のエラー表示を優先する。
      }
    }

    async handleAddError(parsedState, items) {
      const description = typeof parsedState.description === 'string' ? parsedState.description : '';
      await this.syncCartState();
      const matched = items.filter((item) => item.title && description.includes(item.title));
      const failedTitles = matched.length === 1 ? [matched[0].title] : items.map((item) => item.title);
      const message = this.joinMessages(description || this.dataset.msgError, this.dataset.msgPartial);
      this.showError(message, failedTitles);
      document.dispatchEvent(new CustomEvent('product-ajax:error', { detail: { errorMessage: message } }));
    }

    addSelectedToCart() {
      if (this.busy) return;
      this.clearMessages();

      const items = this.collectItems();
      if (items.length === 0) {
        this.showError(this.dataset.msgNone || '');
        this.refreshSubmit();
        return;
      }

      const payloadItems = items.map((item) => ({ id: item.id, quantity: item.quantity }));
      const routes = window.FoxTheme && window.FoxTheme.routes;
      const settings = (window.FoxTheme && window.FoxTheme.settings) || {};
      const utils = window.FoxTheme && window.FoxTheme.utils;

      if (!routes || !routes.cart_add_url || !routes.cart_url || !utils) {
        this.showError(this.dataset.msgError || '');
        return;
      }

      if (document.body.classList.contains('cart-template') || (settings.cartType !== 'drawer' && settings.cartType !== 'popup')) {
        this.setLoading(true);
        utils.postLink(routes.cart_add_url, { parameters: { items: payloadItems } });
        return;
      }

      this.setLoading(true);
      this.lastClickedElement = this.submitButton;

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items: payloadItems,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      fetch(String(routes.cart_add_url), { ...utils.fetchConfig('javascript'), body: body })
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            await this.handleAddError(parsedState, items);
            return;
          }

          const cartResponse = await fetch(String(routes.cart_url), { ...utils.fetchConfig() });
          const cartJson = await cartResponse.json();
          cartJson.sections = parsedState.sections;

          if (window.FoxTheme.pubsub && window.FoxTheme.pubsub.PUB_SUB_EVENTS) {
            window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          }
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

          items.forEach((item) => {
            const input = this.quantityInput(item.row);
            if (input) input.value = '0';
          });
          this.showStatus(this.dataset.msgAdded || '');

          if (settings.cartType === 'popup' && window.PnxAddedModal) {
            window.PnxAddedModal.show();
          } else if (this.cartDrawerElement && typeof this.cartDrawerElement.show === 'function') {
            this.cartDrawerElement.show(this.lastClickedElement);
          }
        })
        .catch(async () => {
          await this.syncCartState();
          this.showError(
            this.joinMessages(this.dataset.msgError, this.dataset.msgPartial),
            items.map((item) => item.title)
          );
        })
        .finally(() => {
          this.setLoading(false);
        });
    }
  }

  customElements.define('pnx-article-bundle-purchase', PnxArticleBundlePurchase);
}
